const WIDE_QUERY = '(min-width: 1280px) and (min-height: 720px) and (hover: hover) and (pointer: fine)';
const DESKTOP_QUERY = '(min-width: 1024px)';

class KodaProductStage extends HTMLElement {
  #abortController = new AbortController();
  #resizeObserver = new ResizeObserver(() => this.#refresh());
  #wideQuery = window.matchMedia(WIDE_QUERY);
  #desktopQuery = window.matchMedia(DESKTOP_QUERY);
  #wheelQueue = [];
  #wheelFrame = 0;
  #indicatorFrame = 0;
  #idleTimeout;
  #accordionCollapseState = new WeakMap();

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#wideQuery.addEventListener('change', this.#refresh, { signal });
    this.#desktopQuery.addEventListener('change', this.#refresh, { signal });
    this.addEventListener('wheel', this.#handleWheel, { passive: false, signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('scroll', this.#handleScroll, { capture: true, passive: true, signal });
    this.addEventListener('pointermove', this.#handleZoomPointer, { passive: true, signal });
    this.addEventListener('pointerleave', this.#clearHoverZoom, { passive: true, signal });
    this.addEventListener('click', this.#handleAccordionClick, { capture: true, signal });
    this.addEventListener('toggle', this.#handleAccordionToggle, { capture: true, signal });
    this.closest('.shopify-section')?.addEventListener('variant:update', this.#handleVariantUpdate, { signal });

    this.#resizeObserver.observe(this);
    this.#refresh();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#resizeObserver.disconnect();
    cancelAnimationFrame(this.#wheelFrame);
    cancelAnimationFrame(this.#indicatorFrame);
    this.#wheelFrame = 0;
    this.#indicatorFrame = 0;
    this.#wheelQueue = [];
    clearTimeout(this.#idleTimeout);
  }

  get leftRail() {
    return this.querySelector('[data-pdp-left-rail]');
  }

  get rightRail() {
    return this.querySelector('[data-pdp-right-rail]');
  }

  get storyContent() {
    return this.querySelector('[data-pdp-story-content]');
  }

  #refresh = () => {
    const story = this.storyContent;
    const storySource = this.querySelector('[data-pdp-story-source]');
    const desktopSlot = this.querySelector('[data-pdp-desktop-story-slot]');
    const isDesktop = this.#desktopQuery.matches;
    const isWide =
      isDesktop && this.#wideQuery.matches && this.dataset.enableDualScroll === 'true';

    this.dataset.pdpMode = isWide ? 'wide' : isDesktop ? 'standard' : 'mobile';

    if (story && desktopSlot && storySource) {
      const destination = isDesktop ? desktopSlot : storySource;
      if (story.parentElement !== destination) destination.append(story);
    }

    this.#clearHoverZoom();
    this.#updateIndicators();
  };

  #normaliseDelta(event) {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 16;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * window.innerHeight;
    return event.deltaY;
  }

  #canConsume(element, delta) {
    if (!(element instanceof HTMLElement) || delta === 0) return false;
    const max = Math.max(0, element.scrollHeight - element.clientHeight);
    return delta > 0 ? element.scrollTop < max - 0.5 : element.scrollTop > 0.5;
  }

  #consume(element, delta) {
    if (!(element instanceof HTMLElement) || delta === 0) return 0;
    const start = element.scrollTop;
    const max = Math.max(0, element.scrollHeight - element.clientHeight);
    element.scrollTop = Math.min(max, Math.max(0, start + delta));
    return element.scrollTop - start;
  }

  #isNativeScrollTarget(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(
        'dialog, iframe, select, textarea, input[type="range"], [data-pdp-native-scroll], [role="dialog"], [popover]'
      )
    );
  }

  #handleWheel = (event) => {
    if (this.dataset.pdpMode !== 'wide' || this.#isNativeScrollTarget(event.target)) return;

    const source = event.target instanceof Element ? event.target.closest('[data-pdp-left-rail], [data-pdp-right-rail]') : null;
    if (!(source instanceof HTMLElement)) return;

    const delta = this.#normaliseDelta(event);
    const canConsume =
      this.#canConsume(source, delta) ||
      (source === this.rightRail && this.#canConsume(this.leftRail, delta));

    if (!canConsume) return;

    event.preventDefault();
    this.#wheelQueue.push({ source, delta });
    if (!this.#wheelFrame) this.#wheelFrame = requestAnimationFrame(this.#flushWheelQueue);
  };

  #handleKeydown = (event) => {
    if (this.dataset.pdpMode !== 'wide') return;
    const source = event.target;
    if (source !== this.leftRail && source !== this.rightRail) return;

    let delta = 0;
    switch (event.key) {
      case 'ArrowDown':
        delta = 40;
        break;
      case 'ArrowUp':
        delta = -40;
        break;
      case 'PageDown':
        delta = source.clientHeight * 0.9;
        break;
      case 'PageUp':
        delta = source.clientHeight * -0.9;
        break;
      case ' ':
        delta = source.clientHeight * (event.shiftKey ? -0.9 : 0.9);
        break;
      case 'Home':
        delta = -source.scrollTop;
        break;
      case 'End':
        delta = source.scrollHeight - source.clientHeight - source.scrollTop;
        break;
      default:
        return;
    }

    event.preventDefault();
    const canConsume =
      this.#canConsume(source, delta) ||
      (source === this.rightRail && this.#canConsume(this.leftRail, delta));

    if (!canConsume) {
      window.scrollBy({ top: delta, behavior: 'auto' });
      return;
    }

    this.#wheelQueue.push({ source, delta });
    if (!this.#wheelFrame) this.#wheelFrame = requestAnimationFrame(this.#flushWheelQueue);
  };

  #flushWheelQueue = () => {
    const entries = this.#wheelQueue.splice(0);
    this.#wheelFrame = 0;

    entries.forEach(({ source, delta }) => {
      let remaining = delta - this.#consume(source, delta);
      if (remaining && source === this.rightRail) {
        remaining -= this.#consume(this.leftRail, remaining);
      }
      if (Math.abs(remaining) > 0.5) window.scrollBy({ top: remaining, behavior: 'auto' });
    });

    this.#markActive();
    this.#updateIndicators();
  };

  #handleScroll = (event) => {
    if (event.target !== this.leftRail && event.target !== this.rightRail) return;
    this.#clearHoverZoom();
    this.#markActive();
    this.#updateIndicators();
  };

  #markActive() {
    this.toggleAttribute('data-scroll-active', true);
    clearTimeout(this.#idleTimeout);
    this.#idleTimeout = setTimeout(() => this.removeAttribute('data-scroll-active'), 1200);
  }

  #updateIndicators() {
    if (this.#indicatorFrame) return;
    this.#indicatorFrame = requestAnimationFrame(() => {
      this.#indicatorFrame = 0;
      [this.leftRail, this.rightRail].forEach((rail) => {
        if (!(rail instanceof HTMLElement)) return;
        const indicator = rail.querySelector('[data-pdp-scroll-indicator]');
        if (!(indicator instanceof HTMLElement)) return;
        const max = Math.max(0, rail.scrollHeight - rail.clientHeight);
        const progress = max > 0 ? rail.scrollTop / max : 0;
        indicator.toggleAttribute('hidden', max <= 1 || this.dataset.showScrollIndicators !== 'true');
        indicator.style.setProperty('--koda-scroll-progress', String(progress));
      });
    });
  }

  #handleZoomPointer = (event) => {
    if (
      this.dataset.enableHoverZoom !== 'true' ||
      this.dataset.pdpMode === 'mobile' ||
      !(event.target instanceof HTMLImageElement)
    ) return;

    const container = event.target.closest('.product-media-container--zoomable');
    if (!(container instanceof HTMLElement) || !container.closest('[data-pdp-left-rail]')) return;

    const bounds = container.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;
    container.style.setProperty('--koda-zoom-x', `${Math.max(0, Math.min(100, x))}%`);
    container.style.setProperty('--koda-zoom-y', `${Math.max(0, Math.min(100, y))}%`);
    container.setAttribute('data-koda-hover-zoom', 'true');
  };

  #clearHoverZoom = () => {
    this.querySelectorAll('[data-koda-hover-zoom]').forEach((element) => {
      element.removeAttribute('data-koda-hover-zoom');
    });
  };

  #getAccordionStickyTop(summary) {
    const computedTop = Number.parseFloat(getComputedStyle(summary).top);
    if (Number.isFinite(computedTop)) return Math.max(0, computedTop);

    const styles = getComputedStyle(document.body);
    const headerGroupHeight = Number.parseFloat(styles.getPropertyValue('--header-group-height'));
    const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height'));
    return Math.max(0, (headerGroupHeight || headerHeight || 0) - 1);
  }

  #handleAccordionClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const summary = event.target.closest('.koda-pdp-accordion__summary');
    const accordion = summary?.closest('.koda-pdp-accordion');
    if (!(summary instanceof HTMLElement) || !(accordion instanceof HTMLDetailsElement) || !accordion.open) return;

    const stickyTop = this.#getAccordionStickyTop(summary);
    const summaryTop = summary.getBoundingClientRect().top;
    const wasStuck = Math.abs(summaryTop - stickyTop) <= 3;
    const rail = this.dataset.pdpMode === 'wide' ? summary.closest('[data-pdp-left-rail]') : null;

    this.#accordionCollapseState.set(accordion, {
      wasStuck,
      rail: rail instanceof HTMLElement ? rail : null,
    });
  };

  #handleAccordionToggle = (event) => {
    const accordion = event.target;
    if (!(accordion instanceof HTMLDetailsElement) || !accordion.matches('.koda-pdp-accordion')) return;

    const state = this.#accordionCollapseState.get(accordion);
    this.#accordionCollapseState.delete(accordion);
    if (accordion.open || !state?.wasStuck) return;

    requestAnimationFrame(() => {
      const summary = accordion.querySelector('.koda-pdp-accordion__summary');
      if (!(summary instanceof HTMLElement)) return;

      const stickyTop = this.#getAccordionStickyTop(summary);
      const delta = summary.getBoundingClientRect().top - stickyTop;
      if (Math.abs(delta) <= 1) return;

      if (state.rail instanceof HTMLElement) {
        state.rail.scrollBy({ top: delta, behavior: 'auto' });
        this.#updateIndicators();
      } else {
        window.scrollBy({ top: delta, behavior: 'auto' });
      }
    });
  };

  #handleVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    if (!(html instanceof Document || html instanceof DocumentFragment || html instanceof Element)) return;

    ['[data-koda-variant-details-region]'].forEach((selector) => {
      const current = this.querySelector(selector);
      const replacement = html.querySelector(selector);
      if (current && replacement) current.replaceWith(replacement.cloneNode(true));
      else if (current && !replacement) current.remove();
    });

    this.#clearHoverZoom();
    requestAnimationFrame(this.#refresh);
  };
}

if (!customElements.get('koda-product-stage')) {
  customElements.define('koda-product-stage', KodaProductStage);
}
